/**
 * "Type or speak" and "Import" — the two ways to give the app rules in the
 * organiser's own words (phase 4). Both start a reading on the server and
 * hand its id to ReadingSheet.
 *
 * Speaking: the phone keyboard's microphone works in the box with no extra
 * code; where the browser has speech recognition (Chrome on Android and
 * desktop) a mic button fills the box too. Photos are shrunk on the device
 * (about 1,600 px) before upload.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { readApi, errorText } from './api';

const MAX_CHARS = 4000;
const NOTICE = "Your rule text is sent to an AI service to be read. Don't include phone numbers or personal details.";
const LANGS = [['en-IN', 'English'], ['hi-IN', 'हिन्दी'], ['mr-IN', 'मराठी']];

const Notice = () => (
  <p className="text-[11px] text-gray-500 leading-snug"><i className="ph ph-shield-check" /> {NOTICE}</p>
);

function useSpeech(onText) {
  const Recognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  useEffect(() => () => rec.current?.abort?.(), []);
  const start = (lang) => {
    if (!Recognition) return;
    const r = new Recognition();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      const said = Array.from(e.results).slice(e.resultIndex).filter((x) => x.isFinal).map((x) => x[0].transcript).join(' ');
      if (said.trim()) onText(said.trim());
    };
    r.onerror = (e) => {
      setListening(false);
      if (e.error === 'not-allowed') toast.error('Allow the microphone to speak your rules, or use your keyboard’s mic.');
    };
    r.onend = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  };
  const stop = () => { rec.current?.stop(); setListening(false); };
  return { supported: !!Recognition, listening, start, stop };
}

export function TypeOrSpeak({ eventId, onStarted, onPlainText }) {
  const [text, setText] = useState('');
  const [lang, setLang] = useState('en-IN');
  const [spoken, setSpoken] = useState(false);
  const [busy, setBusy] = useState(false);
  const speech = useSpeech((said) => {
    setSpoken(true);
    setText((t) => `${t}${t && !/\s$/.test(t) ? ' ' : ''}${said}.`.slice(0, MAX_CHARS));
  });

  const read = async () => {
    speech.stop();
    setBusy(true);
    try {
      const out = await readApi.readText(eventId, text.trim(), spoken);
      onStarted(out.import.id);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="type-or-speak">
      <p className="text-sm text-gray-600">Write your rules the way you'd send them on WhatsApp — English, Hinglish, Hindi or Marathi. Paste a whole message if you like.</p>
      <div className="relative">
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))} data-testid="read-text"
          placeholder={'e.g. 6 overs each side. 3rd ball of every over power ball, runs double, -5 on wicket. No ball 1 run + free hit.'}
          className={`w-full min-h-[180px] p-3 pr-12 rounded-xl border text-[15px] leading-relaxed ${speech.listening ? 'border-[#E54562]' : 'border-[#3AAFA9]'}`} />
        {speech.supported && (
          <button type="button" aria-label={speech.listening ? 'Stop listening' : 'Speak your rules'}
            onClick={() => (speech.listening ? speech.stop() : speech.start(lang))}
            className={`absolute right-2 top-2 w-10 h-10 rounded-full flex items-center justify-center text-lg ${speech.listening ? 'bg-[#E54562] text-white animate-pulse' : 'bg-[#E9EBF1] text-[#2B3A67]'}`}
            data-testid="read-mic">
            <i className={`ph-bold ${speech.listening ? 'ph-stop' : 'ph-microphone'}`} />
          </button>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {speech.supported ? (
          <div className="flex gap-1">
            {LANGS.map(([code, label]) => (
              <button key={code} type="button" onClick={() => setLang(code)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${lang === code ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200 text-[#2B3A67]'}`}>
                {label}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500"><i className="ph ph-microphone" /> Tip: tap your keyboard's mic to speak.</p>
        )}
        <span className={`text-xs ${text.length > MAX_CHARS - 200 ? 'text-[#B3263F] font-bold' : 'text-gray-400'}`}>{text.length} / {MAX_CHARS}</span>
      </div>
      <Notice />
      <button type="button" onClick={read} disabled={busy || !text.trim()}
        className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-40" data-testid="read-go">
        {busy ? 'Sending…' : 'Read my rules'}
      </button>
      <button type="button" onClick={onPlainText} className="w-full text-sm font-semibold text-[#1F7A75]" data-testid="add-text-rule">
        Or add one rule as plain rulebook text
      </button>
    </div>
  );
}

/** A photo, at most 1,600 px on the long side, as JPEG. Falls back to the original. */
async function shrinkImage(file) {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    return blob ? { blob, name: file.name.replace(/\.\w+$/, '') + '.jpg' } : { blob: file, name: file.name };
  } catch {
    return { blob: file, name: file.name };
  }
}

export function ImportRules({ eventId, onStarted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const camera = useRef(null);
  const gallery = useRef(null);
  const picker = useRef(null);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const choose = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('That file is too big (10 MB at most).');
      return;
    }
    setFile(f);
    setPreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };

  const read = async () => {
    setBusy(true);
    try {
      const { blob, name } = file.type.startsWith('image/') ? await shrinkImage(file) : { blob: file, name: file.name };
      const out = await readApi.readFile(eventId, blob, name);
      onStarted(out.import.id);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const big = 'flex-1 min-w-[96px] h-20 rounded-2xl border-2 border-dashed border-[#3AAFA9] text-[#2B3A67] text-xs font-bold flex flex-col items-center justify-center gap-1 hover:bg-[#E6F5F4]';
  return (
    <div className="space-y-3" data-testid="import-rules">
      <p className="text-sm text-gray-600">Import a poster, a photo of printed rules, a PDF or a Word file. We read it and show each rule as a card to check.</p>
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={choose} />
      <input ref={gallery} type="file" accept="image/*" className="hidden" onChange={choose} />
      <input ref={picker} type="file" className="hidden" onChange={choose} data-testid="import-file-input"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" />
      <div className="flex gap-2 flex-wrap">
        <button type="button" className={big} onClick={() => camera.current?.click()}><i className="ph-bold ph-camera text-2xl" />Take a photo</button>
        <button type="button" className={big} onClick={() => gallery.current?.click()}><i className="ph-bold ph-image text-2xl" />Choose a photo</button>
        <button type="button" className={big} onClick={() => picker.current?.click()}><i className="ph-bold ph-file-text text-2xl" />PDF or Word</button>
      </div>
      {file && (
        <div className="rounded-2xl border border-gray-100 p-3 flex items-center gap-3" data-testid="import-preview">
          {preview
            ? <img src={preview} alt="" className="w-20 h-24 object-cover rounded-lg border border-gray-100" />
            : <span className="w-14 h-16 rounded-lg bg-[#E9EBF1] flex items-center justify-center text-2xl text-[#2B3A67]"><i className={`ph-bold ${/\.pdf$/i.test(file.name) ? 'ph-file-pdf' : 'ph-file-doc'}`} /></span>}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold truncate">{file.name}</p>
            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</p>
            <button type="button" className="text-xs font-semibold text-[#B3263F] mt-1" onClick={() => { setFile(null); setPreview(null); }}>Remove</button>
          </div>
        </div>
      )}
      <p className="text-[11px] text-gray-500">Photos and scanned pages are read twice; where the two readings differ, the card asks you. Up to 5 scanned pages per PDF. Files aren't kept after reading.</p>
      <Notice />
      <button type="button" onClick={read} disabled={busy || !file}
        className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-40" data-testid="import-go">
        {busy ? 'Uploading…' : 'Read it'}
      </button>
    </div>
  );
}
